import { mdiInformationOutline } from '@mdi/js';
import Icon from '@mdi/react';
import React from 'react';
import * as S from './claim-live-status-card.styles';

interface IClaimLiveStatusCardProps {
    step?: string;
    stepLabel?: string;
    action?: string;
    actionLabel?: string;
    actionDescription?: string;
    workflowStatus?: string;
    workflowStatusLabel?: string;
    hasLoop?: boolean;
}

interface ILiveBadgeInput {
    workflowStatus?: string;
    workflowStep?: string;
    action?: string;
    hasLoop?: boolean;
}

interface ILiveCardItem {
    workflowStatus?: string;
    workflowStatusLabel?: string;
    workflowStep?: string;
    workflowStepLabel?: string;
    action?: string;
    actionLabel?: string;
    actionDescription?: string;
    hasLoop?: boolean;
}

const EMPTY_VALUE = '-';
const PAUSED_TITLE = 'Paused';
const PAUSED_DESCRIPTION = 'The workers are paused until something changes on the claim';
const WORKERS_PAUSED_TOKENS = new Set(['UNKNOWN', 'PAUSED']);

const isWorkersPausedToken = (token: string): boolean => WORKERS_PAUSED_TOKENS.has(token);

export const isPausedState = (
    workflowStatus?: string,
    workflowStep?: string,
    action?: string,
): boolean => {
    const statusToken = (workflowStatus || '').toUpperCase();
    const stepToken = (workflowStep || '').toUpperCase();
    const actionToken = (action || '').toUpperCase();
    return (
        isWorkersPausedToken(statusToken) &&
        isWorkersPausedToken(stepToken) &&
        isWorkersPausedToken(actionToken)
    );
};

const getLiveBadge = ({
    workflowStatus,
    workflowStep,
    action,
    hasLoop = false,
}: ILiveBadgeInput): S.StatusIndicator => {
    const statusToken = (workflowStatus || '').toUpperCase();
    const stepToken = (workflowStep || '').toUpperCase();
    const actionToken = (action || '').toUpperCase();
    const combined = `${statusToken} ${stepToken} ${actionToken}`;

    if (isPausedState(workflowStatus, workflowStep, action)) {
        return 'awaiting';
    }

    if (combined.includes('HALTED')) {
        return 'actionRequired';
    }

    if (hasLoop) {
        return 'loopDetected';
    }

    if (combined.includes('ERROR')) {
        return 'actionRequired';
    }

    if (combined.includes('WAITING')) {
        return 'awaiting';
    }

    const isInProgressStatus =
        combined.includes('EXECUTE_') ||
        combined.includes('WAIT_FOR_') ||
        combined.includes('NEED_') ||
        combined.includes('HIGH_VALUE_CLAIM_SPLIT') ||
        combined.includes('TIMELY_FILING') ||
        combined.includes('RETRANSMIT');

    if (isInProgressStatus) {
        return 'inProgress';
    }

    return 'complete';
};

const getLiveTitle = (
    workflowStepLabel: string | undefined,
    actionLabel: string | undefined,
    workflowStatusLabel: string | undefined,
    workflowStatus: string | undefined,
    workflowStep: string | undefined,
    action: string | undefined,
): string => {
    const isPaused = isPausedState(workflowStatus, workflowStep, action);
    const title =
        workflowStepLabel ||
        actionLabel ||
        workflowStatusLabel ||
        (isPaused ? PAUSED_TITLE : EMPTY_VALUE);

    if (isPaused) {
        return `Claim Paused: ${title}`;
    }

    if ((workflowStatus || '').toUpperCase().includes('HALTED')) {
        return `Claim Halted: ${title}`;
    }

    return title;
};

const getLiveSubtitle = (
    actionDescription: string | undefined,
    workflowStatus: string | undefined,
    workflowStep: string | undefined,
    action: string | undefined,
): string => {
    if (actionDescription) {
        return actionDescription;
    }

    if (isPausedState(workflowStatus, workflowStep, action)) {
        return PAUSED_DESCRIPTION;
    }

    return EMPTY_VALUE;
};

const getStatusLabel = (indicator: S.StatusIndicator, isPaused = false): string => {
    if (indicator === 'actionRequired') {
        return 'Action required';
    }
    if (indicator === 'loopDetected') {
        return 'Loop detected';
    }
    if (indicator === 'awaiting') {
        return isPaused ? PAUSED_TITLE : 'Awaiting';
    }
    if (indicator === 'complete') {
        return 'Complete';
    }
    return 'In progress';
};

export const mapLiveStatusCard = (
    item: ILiveCardItem,
): { badge: S.StatusIndicator; title: string; subtitle: string; statusLabel: string } => {
    const isPaused = isPausedState(item.workflowStatus, item.workflowStep, item.action);
    const badge = getLiveBadge({
        workflowStatus: item.workflowStatus,
        workflowStep: item.workflowStep,
        action: item.action,
        hasLoop: Boolean(item.hasLoop),
    });

    return {
        badge,
        title: getLiveTitle(
            item.workflowStepLabel,
            item.actionLabel,
            item.workflowStatusLabel,
            item.workflowStatus,
            item.workflowStep,
            item.action,
        ),
        subtitle: getLiveSubtitle(item.actionDescription, item.workflowStatus, item.workflowStep, item.action),
        statusLabel: getStatusLabel(badge, isPaused),
    };
};

export const ClaimLiveStatusCard: React.FC<IClaimLiveStatusCardProps> = ({
    step,
    stepLabel,
    action,
    actionLabel,
    actionDescription,
    workflowStatus,
    workflowStatusLabel,
    hasLoop = false,
}) => {
    const liveCard = mapLiveStatusCard({
        workflowStatus,
        workflowStatusLabel,
        workflowStep: step,
        workflowStepLabel: stepLabel,
        action,
        actionLabel,
        actionDescription,
        hasLoop,
    });

    return (
        <S.Card>
            <S.HeaderRow>
                <S.Eyebrow>Live Status</S.Eyebrow>
                <S.StatusPill $indicator={liveCard.badge}>{liveCard.statusLabel}</S.StatusPill>
            </S.HeaderRow>
            <S.Title>{liveCard.title}</S.Title>
            <S.Subtitle>
                <Icon path={mdiInformationOutline} size={0.6} />
                {liveCard.subtitle}
            </S.Subtitle>
        </S.Card>
    );
};
